import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import {
  formatAxiosAdminError,
  getAdminServicesShop,
  patchAdminServiceShop,
  type AdminServiceShopRow,
} from '@/api'
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure'
import { Loader2 } from 'lucide-react'
import { useHaptic } from '@/hooks/useHaptic'

type Props = {
  catalogWrite: boolean
  token: string | null
}

export function ServicesAdminPanel({ catalogWrite, token }: Props) {
  const haptic = useHaptic()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<AdminServiceShopRow[]>([])
  const [drafts, setDrafts] = useState<Record<number, Partial<AdminServiceShopRow>>>({})
  const [savingId, setSavingId] = useState<number | null>(null)

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const data = await getAdminServicesShop()
      setRows(Array.isArray(data) ? data : [])
      setDrafts({})
    } catch (e) {
      setRows([])
      setError(formatAxiosAdminError(e, 'Не удалось загрузить услуги.'))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const eff = (r: AdminServiceShopRow) => ({ ...r, ...drafts[r.id] })

  const updateDraft = (id: number, patch: Partial<AdminServiceShopRow>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  const save = async (id: number) => {
    const d = drafts[id]
    if (!d || !catalogWrite) return
    setSavingId(id)
    try {
      const next = await patchAdminServiceShop(id, d)
      setRows((prev) => prev.map((x) => (x.id === id ? next : x)))
      setDrafts((prev) => {
        const x = { ...prev }
        delete x[id]
        return x
      })
      haptic.selection()
    } catch (e) {
      if (axios.isAxiosError(e)) {
        alert(formatAxiosAdminError(e, 'Не удалось сохранить.'))
      }
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <AdminGlassDisclosure
        accent="violet"
        density="compact"
        title="Услуги (вкладка каталога)"
        subtitle="Название и текст цены как на витрине"
        badge={`${rows.length}`}
      >
        <p className="text-[11px] mb-3" style={{ color: 'var(--text-2)' }}>
          Цена задаётся строкой — можно «от 4 990 ₽» или произвольный текст, как клиент должен видеть.
        </p>
        {error && (
          <div
            className="rounded-xl px-3 py-2 text-xs mb-3"
            style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
          >
            {error}
            <button type="button" className="underline ml-2 press" onClick={() => void load()}>
              Обновить
            </button>
          </div>
        )}
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin" size={24} style={{ color: 'var(--cyan)' }} />
          </div>
        )}
        {!loading && (
          <div className="space-y-2">
            {[...rows]
              .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
              .map((raw) => {
                const r = eff(raw)
                const changed = Object.keys(drafts[raw.id] ?? {}).length > 0
                return (
                  <div key={raw.id} className="glass rounded-xl p-3 space-y-2">
                    <div className="grid grid-cols-1 gap-2">
                      <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                        Услуга
                        <input
                          className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-sm"
                          disabled={!catalogWrite}
                          value={r.name}
                          onChange={(e) => updateDraft(raw.id, { name: e.target.value })}
                        />
                      </label>
                      <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                        Цена (текст)
                        <input
                          className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-sm"
                          disabled={!catalogWrite}
                          value={r.price ?? ''}
                          placeholder="напр. от 9 990 ₽"
                          onChange={(e) => updateDraft(raw.id, { price: e.target.value.trim() || null })}
                        />
                      </label>
                      <label className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-2)' }}>
                        <input
                          type="checkbox"
                          disabled={!catalogWrite}
                          checked={r.is_active}
                          onChange={(e) => updateDraft(raw.id, { is_active: e.target.checked })}
                        />
                        На витрине
                      </label>
                      {catalogWrite && (
                        <button
                          type="button"
                          disabled={!changed || savingId === raw.id}
                          className="press py-2 rounded-xl text-xs font-bold disabled:opacity-40"
                          style={{
                            background: 'rgba(72,187,120,0.2)',
                            color: '#68D391',
                            border: '1px solid rgba(72,187,120,0.4)',
                          }}
                          onClick={() => void save(raw.id)}
                        >
                          {savingId === raw.id ? 'Сохраняем…' : changed ? 'Сохранить' : 'Нет изменений'}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>
        )}
      </AdminGlassDisclosure>
    </div>
  )
}
